import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { BackButton, FormGroupControl } from '@components/Base';
import { ServerError } from '@components/Helper';
import { PolicyOverviewCardBody } from './policy-overview-card-body';
import { ContactInfoCardBody } from './contact-info-card-body';
import { PaymentInfoCardBody } from '../HealthPolicyClaim/payment-info-card-body';
import { ClaimCardBody } from './claim-card-body';

import {
  policyFetch,
  userFetch,
  corporateHealthOrderUserRemainingBenefitsFetch,
  corporateHealthClaimCreate,
  CorporateHealthClaimCreatePayload,
} from './action';
import { selectCorporateHealthPolicyClaim } from './selector';
import { FormValues, formValuesSchema } from './schema';
import { useInitialValues } from './hook';
import { InsuranceTypes } from '@models/InsuranceType';

const CorporateHealthPolicyClaim = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orderableId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, corporateHealthOrderUserRemainingBenefits, errors } = useSelector(selectCorporateHealthPolicyClaim);
  const initialValues = useInitialValues();

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: CorporateHealthClaimCreatePayload = {
      ...values,
      corporateHealthOrderId: Number(orderableId),
      corporateUserId: Number(user?.userId),
      insuranceType: InsuranceTypes.Health,
    };
    dispatch(corporateHealthClaimCreate(payload));
    setSubmitting(false);
  };

  useEffect(() => {
    dispatch(policyFetch({ id: orderableId, username: searchParams.get('username') }));
  }, [orderableId, searchParams]);

  useEffect(() => {
    dispatch(userFetch({ id: orderableId, username: searchParams.get('username') }));
  }, [orderableId, searchParams]);

  useEffect(() => {
    dispatch(
      corporateHealthOrderUserRemainingBenefitsFetch({ id: orderableId, username: searchParams.get('username') }),
    );
  }, [orderableId, searchParams]);

  return (
    <>
      <BackButton className="mb-4" />

      {initialValues && (
        <Card>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={formValuesSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Card.Header className="fs-5 fw-bold">{t('common:claimForm')}</Card.Header>

                <PolicyOverviewCardBody />
                <ContactInfoCardBody />
                <PaymentInfoCardBody />
                <ClaimCardBody remainingBenefits={corporateHealthOrderUserRemainingBenefits} />

                <Card.Body>
                  <FormGroupControl
                    type="checkbox"
                    name="acceptTermsAndConditions"
                    label={t('claimForm.acceptTermsAndConditions')}
                    bsPrefix="form-check-input"
                    groupProps={{ className: 'form-check form-check-inline' }}
                  />
                </Card.Body>

                <Card.Footer className="bg-white">
                  <div className="text-center my-4">
                    <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                      {t('common:submit')}
                    </Button>
                  </div>
                </Card.Footer>

                <ServerError errors={errors ?? {}} />
              </Form>
            )}
          </Formik>
        </Card>
      )}
    </>
  );
};

export { CorporateHealthPolicyClaim };
